import React, { useEffect } from 'react';
import { AccessibilityInfo, Pressable, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from './Texte';
import { Check } from './icons';
import { useCopie, type EtatCopie } from '../hooks/useCopie';
import { colors, controleLarge, formes, gradients, radius, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

/**
 * `accessibilityLiveRegion` n'existe que sur Android : sur iOS, VoiceOver n'annonce rien d'un libellé
 * qui change. L'annonce explicite couvre les deux, et ne se fait qu'au PASSAGE à un état (jamais au
 * montage ni au retour au repos).
 */
function useAnnonce(etat: EtatCopie, message: string) {
  useEffect(() => {
    if (etat !== 'repos') AccessibilityInfo.announceForAccessibility(message);
    // Le message ne change qu'avec l'état (et la langue, qui remonte tout l'arbre).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etat]);
}

/**
 * « Copier » d'un bloc : pastille à côté du libellé. Au toucher, le texte part dans le
 * presse-papiers et la pastille passe à « Copié » (vert) pendant deux secondes. La zone tactile de
 * 44 pt vient de l'en-tête du bloc ; la pastille reste à 32 pt comme sur la maquette.
 */
export function BoutonCopier({ texte, nom, testID }: { texte: string; nom: string; testID?: string }) {
  const { etat, copier } = useCopie();
  const copie = etat === 'copie';
  const echec = etat === 'echec';
  const libelle = copie ? t('linkedinKit.copied') : echec ? t('linkedinKit.copyFailed') : t('linkedinKit.copy');
  useAnnonce(etat, `${libelle} : ${nom}`);
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={t('linkedinKit.copyLabel', { nom })}
      // Seulement après une copie ou un échec : au repos, la valeur doublerait le libellé
      // (« Copier : Titre, Copier »).
      accessibilityValue={etat === 'repos' ? undefined : { text: libelle }}
      accessibilityLiveRegion="polite"
      hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
      onPress={() => {
        void copier(texte);
      }}
      style={[styles.pastille, copie && styles.pastilleCopie, echec && styles.pastilleEchec]}
    >
      {copie ? <Check size={14} color={colors.success} strokeWidth={2.6} /> : null}
      <Text style={[styles.pastilleTexte, copie && styles.pastilleTexteCopie, echec && styles.pastilleTexteEchec]}>
        {libelle}
      </Text>
    </Pressable>
  );
}

/**
 * « Copier tout le kit » : le grand bouton de la maquette. UN SEUL Pressable dont le fond change
 * (dégradé, puis vert « Kit copié » pendant deux secondes) : le démonter pour en monter un autre
 * ferait perdre le focus à un lecteur d'écran. Un échec s'écrit sous le bouton.
 */
export function BoutonCopierTout({ texte, testID }: { texte: string; testID?: string }) {
  const { etat, copier } = useCopie();
  const copie = etat === 'copie';
  useAnnonce(etat, copie ? t('linkedinKit.kitCopied') : t('linkedinKit.kitCopyFailed'));
  const contenu = copie ? (
    <>
      <Check size={20} color={colors.white} strokeWidth={2.6} />
      <Text style={styles.toutTexte}>{t('linkedinKit.kitCopied')}</Text>
    </>
  ) : (
    <Text style={styles.toutTexte}>{t('linkedinKit.copyAll')}</Text>
  );
  return (
    <View style={styles.toutWrap}>
      <Pressable
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={copie ? t('linkedinKit.kitCopied') : t('linkedinKit.copyAll')}
        accessibilityLiveRegion="polite"
        onPress={() => {
          void copier(texte);
        }}
        style={controleLarge}
      >
        {copie ? (
          <View style={[styles.toutFond, styles.toutFondCopie]}>{contenu}</View>
        ) : (
          <LinearGradient
            colors={gradients.cta}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.toutFond}
          >
            {contenu}
          </LinearGradient>
        )}
      </Pressable>
      {etat === 'echec' ? (
        <Text testID={testID ? `${testID}-echec` : undefined} accessibilityRole="alert" style={styles.toutEchec}>
          {t('linkedinKit.kitCopyFailed')}
        </Text>
      ) : null}
    </View>
  );
}

const HAUTEUR_PASTILLE = 32;

const styles = StyleSheet.create({
  pastille: {
    height: HAUTEUR_PASTILLE,
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    borderWidth: 1.5,
    borderColor: colors.violetVividSoft,
    backgroundColor: colors.white,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  pastilleCopie: { borderColor: colors.success, backgroundColor: colors.successSoft },
  pastilleEchec: { borderColor: colors.error, backgroundColor: colors.white },
  pastilleTexte: { fontSize: 12.5, fontWeight: '800', color: colors.violetProfil },
  // Texte foncé sur fond vert clair : le vert de la charte seul n'aurait pas le contraste voulu à 12 pt.
  pastilleTexteCopie: { color: colors.grey900 },
  pastilleTexteEchec: { color: colors.error },
  toutWrap: { gap: spacing.xs },
  toutFond: {
    height: formes.hauteur.lg,
    borderRadius: radius.pill,
    paddingHorizontal: spacing.xl,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  toutFondCopie: { backgroundColor: colors.success },
  toutTexte: { fontSize: 17, fontWeight: '800', color: colors.white },
  toutEchec: { fontSize: typography.small.size, color: colors.error, textAlign: 'center' },
});
